import { useState } from "react";
import { Box, IconButton, Popover, Typography } from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

export interface HelpTipProps {
  /** Bold first line — usually the control's own label. */
  title: string;
  /** Body. A string, or JSX when the explanation needs structure. */
  children: React.ReactNode;
  /** Accessible name; defaults to "What is <title>?". */
  ariaLabel?: string;
}

/**
 * A "?" beside a control that opens a short explanation.
 *
 * A click-to-open popover rather than a hover tooltip: these explain concepts
 * that take a couple of sentences (percentile vs confidence, what a camera is),
 * and a tooltip that large flickers away while you're still reading it. Popover
 * content also stays selectable and reachable by keyboard.
 */
export default function HelpTip({ title, children, ariaLabel }: HelpTipProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <IconButton
        size="small"
        aria-label={ariaLabel ?? `What is ${title}?`}
        onClick={(event) => {
          // Inside a ToggleButtonGroup or label, the click would otherwise be
          // taken as selecting the control it explains.
          event.stopPropagation();
          event.preventDefault();
          setAnchor(event.currentTarget);
        }}
        sx={{ p: 0.25, color: "text.disabled", "&:hover": { color: "text.secondary" } }}
      >
        <HelpOutlineIcon sx={{ fontSize: 15 }} />
      </IconButton>
      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { maxWidth: 320, p: 1.5 } } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          {title}
        </Typography>
        <Box
          sx={{
            color: "text.secondary",
            fontSize: 13,
            lineHeight: 1.5,
            "& p": { m: 0, mb: 1 },
            "& p:last-of-type": { mb: 0 },
          }}
        >
          {children}
        </Box>
      </Popover>
    </>
  );
}

/** Label + "?" on one line — the shape every rail section header uses. */
export function HelpLabel({
  label,
  title,
  children,
}: {
  label: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <HelpTip title={title}>{children}</HelpTip>
    </Box>
  );
}
